// Resource type names - must match RESOURCE_FORMAT_NAME in index.tsx
export const RESOURCE_FORMAT_NAME = {
  CASE_STUDY: 'case-studies',
  BRAND_ASSETS: 'brand-assets',
  THOUGHT_LEADERSHIP: 'thought-leadership',
  EVENT_RECORDINGS: 'event-recordings',
  CRAFT_ARTICLES: 'articles',
  NOT_SURE: 'miscellaneous',
} as const;

export type RESOURCE_FORMAT_NAME = (typeof RESOURCE_FORMAT_NAME)[keyof typeof RESOURCE_FORMAT_NAME];

export type FieldName =
  | 'title'
  | 'short_description'
  | 'long_description'
  | 'clients_uuids'
  | 'external_client_share_status'
  | 'roles_uuids'
  | 'started_at'
  | 'released_at'
  | 'image'
  | 'additional_images'
  | 'link'
  | 'file'
  | 'resource_format_uuid'
  | 'tags'
  | 'contributors_uuids'
  | 'companies_uuids'
  | 'project_categories_uuids'
  | 'skills_uuids'
  | 'impact_metrics'
  | 'internal_initiative'
  | 'internal_share_status'
  | 'pulse_submission'
  | 'draft'
  | 'has_paywall'
  | 'resource_submission_category_uuid';

export type ResourceTypeRules = {
  show?: Partial<Record<FieldName, boolean>>;
  required?: Partial<Record<FieldName, boolean>>;
  stripOnHide?: FieldName[]; // fields to clear when they become hidden
  titles?: Partial<Record<FieldName, string>>; // custom titles for fields
};

export const DEFAULT_RESOURCE_TYPE_RULES: ResourceTypeRules = {
  show: {},
  required: {},
  stripOnHide: [],
  titles: {},
};

// Default field titles - used as fallback when no custom title is specified
export const DEFAULT_FIELD_TITLES: Partial<Record<FieldName, string>> = {
  title: 'Add a title',
  short_description: 'A one-line preview',
  long_description: 'Main description - describe the work, process and the story',
  clients_uuids: 'Related Clients',
  external_client_share_status: 'External Client Share Status',
  started_at: 'Client work dates',
  released_at: 'End Date (optional)',
  image: 'Upload image(s)',
  additional_images: 'Additional images',
  link: 'Link to where we can see your project / interact with it',
  file: 'Upload File',
  resource_format_uuid: 'Select the file type',
  tags: 'Additional tags to make this work easier to find later',
  contributors_uuids: 'Team members',
  companies_uuids: 'kyu Compani(es)',
  project_categories_uuids: 'How would you categorize the services provided?',
  skills_uuids: 'Skills and tools utilized',
  impact_metrics: 'Impact metrics',
  internal_initiative: 'Internal Initiative',
  internal_share_status: 'Internal Share Status',
  pulse_submission: 'Pulse Submission',
  draft: 'Draft',
  resource_submission_category_uuid: 'Submission Category',
};

// Default behavior: all fields shown by default (undefined = shown)
export const RESOURCE_TYPE_RULES: Record<string, ResourceTypeRules> = {
  [RESOURCE_FORMAT_NAME.CASE_STUDY]: {
    show: {
      started_at: false,
      released_at: false,
      external_client_share_status: false,
      internal_share_status: false,
    },
    required: {
      started_at: false,
      released_at: false,
      external_client_share_status: false,
      internal_share_status: false,
    },
    stripOnHide: [],
  },
  [RESOURCE_FORMAT_NAME.BRAND_ASSETS]: {
    show: {
      clients_uuids: false,
      skills_uuids: false,
      impact_metrics: false,
      project_categories_uuids: false,
      started_at: false,
      released_at: false,
      external_client_share_status: false,
      internal_share_status: false,
    },
    required: {
      clients_uuids: false,
      skills_uuids: false,
      impact_metrics: false,
      project_categories_uuids: false,
      started_at: false,
      released_at: false,
      external_client_share_status: false,
      internal_share_status: false,
    },
    stripOnHide: [],
    titles: {
      long_description: 'Main description - describe how the assets are to be used',
      link: 'Link to where we can see the brand assets',
    },
  },
  [RESOURCE_FORMAT_NAME.THOUGHT_LEADERSHIP]: {
    show: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
    },
    required: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
    },
    stripOnHide: [],
  },
  [RESOURCE_FORMAT_NAME.EVENT_RECORDINGS]: {
    show: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
      project_categories_uuids: false,
    },
    required: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
      project_categories_uuids: false,
    },
    stripOnHide: [],
    titles: {
      long_description: 'Main description - describe the key takeaways from the event',
      link: 'Link to where we can see the recording',
      clients_uuids: 'Clients mentioned',
      skills_uuids: 'Skills and tools referenced',
    },
  },
  [RESOURCE_FORMAT_NAME.CRAFT_ARTICLES]: {
    show: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
      project_categories_uuids: false,
    },
    required: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
      project_categories_uuids: false,
    },
    stripOnHide: [],
    titles: {
      link: 'Link to where we can see the article',
    },
  },
  [RESOURCE_FORMAT_NAME.NOT_SURE]: {
    show: {
      impact_metrics: false,
      started_at: false,
      released_at: false,
    },
    required: {
      clients_uuids: false,
      skills_uuids: false,
      impact_metrics: false,
      project_categories_uuids: false,
      started_at: false,
      released_at: false,
      external_client_share_status: true,
      internal_share_status: true,
    },
    titles: {
      clients_uuids: 'Clients mentioned',
      skills_uuids: 'Skills and tools referenced',
      long_description: 'Main description - describe the key takeaways',
      project_categories_uuids: 'Services referenced',
    },
    stripOnHide: [],
  },
};

export const getRules = (resourceType?: string, bestOfSubmission = false): ResourceTypeRules => {
  const rules = RESOURCE_TYPE_RULES[
    resourceType === 'pitch' ? RESOURCE_FORMAT_NAME.CASE_STUDY : resourceType ?? ''
  ] ?? RESOURCE_TYPE_RULES[RESOURCE_FORMAT_NAME.NOT_SURE];

  if (!bestOfSubmission) return rules;

  return {
    ...rules,
    show: { ...rules.show, internal_share_status: true, external_client_share_status: true },
    required: { ...rules.required, internal_share_status: true, external_client_share_status: true },
  };
};

export const isShown = (rules: ResourceTypeRules, field: FieldName): boolean =>
  rules.show?.[field] !== false;

const DEFAULT_REQUIRED_FIELDS = new Set<FieldName>([
  'title',
  'short_description',
  'long_description',
  'image',
  'resource_format_uuid',
  'contributors_uuids',
  'companies_uuids',
  'clients_uuids',
  'project_categories_uuids',
  'skills_uuids',
  'started_at',
  'internal_share_status',
  'external_client_share_status',
]);

export const isRequired = (rules: ResourceTypeRules, field: FieldName): boolean => {
  const resourceTypeOverride = rules.required?.[field];

  return resourceTypeOverride ?? DEFAULT_REQUIRED_FIELDS.has(field);
};

export const getFieldTitle = (rules: ResourceTypeRules, field: FieldName): string | undefined => {
  // First check if there's a custom title in the rules
  if (rules.titles?.[field]) {
    return rules.titles[field];
  }
  // Otherwise return the default title
  return DEFAULT_FIELD_TITLES[field];
};
